import { useState } from "react";
import {
  Paper,
  Title,
  Text,
  Stack,
  TextInput,
  Button,
  Alert,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { PageHeader } from "../components/PageHeader";
import { showSuccess } from "../lib/notifications";
import { useAuth } from "../contexts/AuthContext";

interface ProfileForm {
  firstName: string;
  lastName: string;
  email: string;
}

export function SettingsPage() {
  const { user } = useAuth();
  const [error, setError] = useState("");

  const profileForm = useForm<ProfileForm>({
    initialValues: {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
    },
    validate: {
      firstName: (value) => (!value ? "First name is required" : null),
      lastName: (value) => (!value ? "Last name is required" : null),
      email: (value) => (/^\S+@\S+$/.test(value) ? null : "Invalid email"),
    },
  });

  const updateProfile = useMutation({
    mutationFn: async (data: ProfileForm) => {
      // TODO: Implement update profile API
    },
    onSuccess: () => {
      showSuccess("Profile updated successfully");
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || "Failed to update profile");
    },
  });

  return (
    <>
      <PageHeader title="Settings" description="Manage your account settings" />

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          mb="md"
          title="Error"
        >
          {error}
        </Alert>
      )}

      <Paper withBorder p="md" radius="md">
        <Title order={3}>Profile Settings</Title>
        <Text color="dimmed" size="sm" mb="lg">
          Update your personal information
        </Text>

        <form
          onSubmit={profileForm.onSubmit((values) =>
            updateProfile.mutate(values)
          )}
        >
          <Stack>
            <TextInput
              label="First Name"
              placeholder="John"
              required
              {...profileForm.getInputProps("firstName")}
            />
            <TextInput
              label="Last Name"
              placeholder="Doe"
              required
              {...profileForm.getInputProps("lastName")}
            />
            <TextInput
              label="Email"
              placeholder="john@example.com"
              required
              disabled
              {...profileForm.getInputProps("email")}
            />
            <Button type="submit" loading={updateProfile.isLoading}>
              Update Profile
            </Button>
          </Stack>
        </form>
      </Paper>
    </>
  );
}
